import React, { useState, useEffect } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { CompetitorAdvantage, CompetitorCategory } from '../types/competitor';
import { Button } from './ui/Button';

interface AdvantageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (advantage: Partial<CompetitorAdvantage>) => void;
  advantage?: CompetitorAdvantage | null;
  title?: string;
}

const AdvantageModal: React.FC<AdvantageModalProps> = ({
  isOpen,
  onClose,
  onSave,
  advantage,
  title = 'Add Advantage'
}) => {
  const [formData, setFormData] = useState<Partial<CompetitorAdvantage>>({
    point: '',
    category: CompetitorCategory.OTHER,
    evidence: ''
  });

  useEffect(() => {
    if (advantage) {
      setFormData({
        point: advantage.point || '',
        category: advantage.category || CompetitorCategory.OTHER,
        evidence: advantage.evidence || ''
      });
    } else {
      setFormData({
        point: '',
        category: CompetitorCategory.OTHER,
        evidence: ''
      });
    }
  }, [advantage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.point?.trim()) {
      onSave(formData);
      onClose();
    }
  };

  const getCategoryLabel = (category: CompetitorCategory): string => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="p-6 w-full max-w-md modal-panel">
        <div className="flex justify-between items-center mb-4">
          <h2 className="heading-3">{title}</h2>
          <Button variant="tertiary" size="sm" onClick={onClose} className="!p-1">
            <CloseIcon />
          </Button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Advantage Point *
              </label>
              <textarea
                value={formData.point}
                onChange={(e) => setFormData({ ...formData, point: e.target.value })}
                className="w-full px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature"
                rows={3}
                required
                placeholder="Describe the advantage..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as CompetitorCategory })}
                className="w-full px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature"
                required
              >
                {Object.values(CompetitorCategory).map((cat) => (
                  <option key={cat} value={cat}>
                    {getCategoryLabel(cat)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Evidence (Optional)
              </label>
              <textarea
                value={formData.evidence}
                onChange={(e) => setFormData({ ...formData, evidence: e.target.value })}
                className="w-full px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature"
                rows={2}
                placeholder="Supporting evidence or data..."
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!formData.point?.trim()}>
              {advantage ? 'Update' : 'Add'} Advantage
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdvantageModal;